<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import DropdownButton from '../components/DropdownButton.vue'
import LibrarySidebarMenu from './LibrarySidebarMenu.vue'
import GbButton from '@/components/GbButton.vue'
import { LibraryContentObject } from './library_types'
import { useAttachments } from './composables/useAttachments'
import RenameFolderModal from '@/library/RenameFolderModal.vue'
import DeleteFolderModal from '@/library/DeleteFolderModal.vue'
import ShareFolderModal from '@/library/ShareFolderModal.vue'
import ArchiveFolderModal from '@/library/ArchiveFolderModal.vue'
import UnarchiveFirstModal from '@/library/UnarchiveFirstModal.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import CopyFolderModal from './CopyFolderModal.vue'
import LibraryFolderContentSection from './LibraryFolderContentSection.vue'
import { useLibraryStore } from './library.store'
import { useUserStore } from './../authentication/user.store'
import ProgressSpinner from 'primevue/progressspinner'
import { toolkitUrls, goalbookUrls } from '@/util/links.js'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { useLibraryFolderAnalytics } from './composables/useLibraryFolderAnalytics'
import RolesAndPermissionsModal from '@/library/RolesAndPermissionsModal.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { ACTION_TYPES, actionPermissions, CONTENT_TYPES } from "@/util/access_visibility.js";
import FolderHeader from './FolderHeader.vue'
import RemoveContributorModal from './RemoveContributorModal.vue'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import AttachmentModal from '@/library/components/AttachmentModal.vue'
import LinkCard from '@/library/components/LinkCard.vue'
import { ToastProvider, ToastRoot, ToastViewport } from 'reka-ui'

const analytics = getAnalyticsTracker()
const libraryStore = useLibraryStore()
const userStore = useUserStore()
const visibilityStore = useVisibilityStore()
const router = useRouter()
const props = defineProps({
  uuid: {
    type: String,
    required: true
  }
})

const {
  loadingResources,
  currentFolder,
  goals,
  strategies,
  combinedPresentLevels,
  impactsOfDisabilities,
  resources,
  attachments,
  loadFolderError,
  loadFolderErrorStatusCode,
  isCurrentFolderArchived,
  hasAddRemoveContributorPerms,
  doesUserOwnCurrentFolder,
} = storeToRefs(libraryStore)
const { isTrial, isLicenseExpired, authInfo, userId } = storeToRefs(userStore)

const shouldShowPremiumAction = computed(() => isTrial.value || isLicenseExpired.value)
const { getLegacyEventData, getEventData } = useLibraryFolderAnalytics()
const { shareVisibility, iodVisibility, aiFeatureVisibility, betaVisibility } = storeToRefs(visibilityStore)
const canOpenShareModal = computed(() => shareVisibility.value && doesUserOwnCurrentFolder.value)

// Sort present levels so v2 cards (pl_student_summary) come first, v1 cards (presentlevel) come last
// Only apply this sorting for users with AI features enabled
const sortedPresentLevels = computed(() => {
  // Users without AI features see statements in original order
  if (!aiFeatureVisibility.value) {
    return combinedPresentLevels.value
  }
  
  return [...combinedPresentLevels.value].sort((a, b) => {
    // v2 cards (pl_student_summary) should come before v1 cards (presentlevel)
    if (a.content_type === 'pl_student_summary' && b.content_type === 'presentlevel') return -1
    if (a.content_type === 'presentlevel' && b.content_type === 'pl_student_summary') return 1
    return 0
  })
})

const openShare = ref(false)
const openRename = ref(false)
const openArchive = ref(false)
const openDelete = ref(false)
const openUnarchiveFirst = ref(false)
const openPremiumAction = ref(false)
const openCopy = ref(false)
const openContributors = ref(false)
const openRemoveContributor = ref(false)

const {
  openAttachmentModal,
  attachmentToEdit,
  showAllAttachments,
  showAttachmentToast,
  attachmentToastMessage,
  attachmentToastError,
  currentFolderAttachmentCount,
  attachmentLimitReached,
  hasMoreAttachments,
  visibleAttachments,
  showAttachmentToastMessage,
  handleAttachmentEdit,
  toggleAttachmentsView,
  openAddAttachmentModal,
  ATTACHMENT_LIMIT_TOOLTIP_TEXT,
  ATTACHMENT_LIMIT_ARIA_DESCRIPTION,
} = useAttachments(attachments)

function handleAddAttachment() {
  if (shouldShowPremiumAction.value) {
    openPremiumAction.value = true
  } else {
    openAddAttachmentModal()
  }
}

function handleAttachmentSaved() {
  showAttachmentToastMessage(attachmentToEdit.value ? 'Attachment updated' : 'Attachment added')
  attachmentToEdit.value = null
  loadFolder()
}

async function handleAttachmentDeleted() {
  showAttachmentToastMessage('Attachment deleted')
  await loadFolder()
}

function handleAttachmentError(payload: { message: string; error: unknown }) {
  console.error(payload.message, payload.error)
  showAttachmentToastMessage('Error saving attachment.', true)
}

const copyEnabled = computed(() => {
  return currentFolder.value && currentFolder.value.name && !copyPermissionsBlocked.value.block
})

const copyPermissionsBlocked = computed(() => {
  return actionPermissions(ACTION_TYPES.COPY_STUDENT, CONTENT_TYPES.STUDENT, {}, authInfo.value)
})

const { LIBRARY } = EVENT_NAMES

function openModal(modalName: string) {
  switch (modalName) {
    case 'rename':
      openRename.value = true
      analytics.sendEvent('Clicked Edit Student Button', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent('Clicked Edit Folder Button', getEventData.value)
      break
    case 'archive':
      openArchive.value = true
      analytics.sendEvent('Clicked Archive Folder', getEventData.value)
      break
    case 'unarchiveFirst':
      openUnarchiveFirst.value = true
      analytics.sendEvent('Attempted Open Archive Folder', getEventData.value)
      break
    case 'delete':
      openDelete.value = true
      analytics.sendEvent('Clicked Delete Student Button', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent('Clicked Delete Folder Button', getEventData.value)
      break
    case 'share':
      if (canOpenShareModal.value) {
        openShare.value = true
        analytics.sendEvent('Clicked Share Student', getLegacyEventData.value) // to be deprecated
        analytics.sendEvent('Clicked Share Folder', getEventData.value)
      }
      break
    case 'premiumAction':
      openPremiumAction.value = true
      break
    case 'contributors':
      if (shouldShowPremiumAction.value) {
        openPremiumAction.value = true
      } else {
        openContributors.value = true
        analytics.sendEvent(LIBRARY.FOLDER_ACTION.OPENED_SHARE_FOLDER_MODAL, getEventData.value)
      }
      break
    case 'copy':
      openCopy.value = true
      analytics.sendEvent('Clicked Copy Folder Button', { access: copyEnabled.value })
      analytics.sendEvent('Clicked Copy Student Button', { access: copyEnabled.value })
      if (!copyEnabled.value) {
        openPremiumAction.value = true
        return
      }
      break
    case 'removeSelfAsContributor':
      openRemoveContributor.value = true
      break
  }
}

const manageDropdownItems = computed(() => {
  const baseItems = [
    {
      label: 'Rename',
      command: () => {
        openModal('rename')
      }
    },
    {
      label: 'Archive',
      command: () => {
        if (shouldShowPremiumAction.value) {
          openModal('premiumAction')
        } else {
          openModal('archive')
        }
      }
    },
    {
      label: 'Delete',
      command: () => {
        openModal('delete')
      }
    },
    {
      label: 'Copy',
      command: () => {
        openModal('copy')
      }
    }
  ]

  return baseItems
})


const loadFolder = async function () {
  await libraryStore.fetchResourcesForFolder({
    folderUUID: props.uuid,
    params: {}
  })
}

const handleFolderArchived = () => {
  router.push({ name: 'AllFolders' })
}

watch(() => openUnarchiveFirst.value, (newValue) => {
  if (!newValue) {
    router.push({ name: 'ArchivedFolders' })
  }
})

// Watch for RolesAndPermissionsModal closing and refresh folder data
watch(() => openContributors.value, (newValue, oldValue) => {
  // When modal closes (newValue is false and oldValue was true)
  if (!newValue && oldValue) {
    loadFolder()
  }
})

onMounted(async function () {
  await loadFolder()

  if (isCurrentFolderArchived.value) {
    openModal('unarchiveFirst')
  } else {
    analytics.sendEvent('Viewed Student Library Page', getLegacyEventData.value)
    analytics.sendEvent('Viewed Folder Library Page', getEventData.value)
  }
})
</script>
<template>
  <div class="container">
    <div v-if="!isCurrentFolderArchived" class="grid grid-cols-12 gap-4 pt-12">
      <div class="col-span-12 py-4 pl-4 pr-8 md:col-span-4 lg:col-span-3">
        <LibrarySidebarMenu />
      </div>
      <div class="col-span-12 p-4 md:col-span-8 lg:col-span-9">
        <div v-if="loadingResources && !loadFolderError" class="pl-4 inline-flex items-center">
          <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-primary-400" />
          <span class="ml-2">Loading resources...</span>
        </div>
        <div v-else-if="!loadingResources && loadFolderError && (loadFolderErrorStatusCode === 403 || loadFolderErrorStatusCode === 404)">
          <p>This folder was not found or you do not have access to it.</p>
        </div>
        <div v-else-if="!loadingResources && loadFolderError">
          <p>There was an error loading this folder. Please check the URL or try again later.</p>
        </div>
        <div  v-else>
          <div class="flex flex-col lg:flex-row justify-between items-start gap-4">
            <div class="min-w-0 flex-1 w-full">
              <FolderHeader />
            </div>
            <div class="flex items-center gap-2 mt-2 lg:mt-0">
              <DropdownButton
                v-if="currentFolder.owner === userId"
                buttonAriaLabel="Manage folder options"
                :items="manageDropdownItems"
                buttonTheme="grayOutlineSecondary"
                dropdownId="manageFolderDropdown"
                :additional-classes="'h-[43px] w-[62px] flex items-center rounded border border-gray-400 bg-white py-3 pr-1.5 pl-2'"
              >
                <template #buttonContents>
                  <span class="material-icons-outlined flex-shrink-0 text-gray-700 text-[24px]">settings</span>
                  <span class="material-icons-round leading-[0] text-gray-700"> arrow_drop_down</span>
                </template>
              </DropdownButton>
              <GbButton
                v-if="!hasAddRemoveContributorPerms"
                class="whitespace-nowrap"
                label="Copy Folder"
                gbTheme="grayOutlineSecondary"
                @click="openModal('copy')"
              />
              <GbButton
                class="whitespace-nowrap border-t-2 border-primary-400 mt-2 md:mt-0"
                gb-theme="bluePrimary"
                @click="openModal('contributors')"
              >
                <span class="material-icons-outlined leading-[0] pr-2">group</span>
                <span>Share Folder</span>
              </GbButton>
            </div>
          </div>

          <div class="grid grid-cols-12 gap-[68px]">
            <div class="col-span-12 lg:col-span-8">
              <LibraryFolderContentSection
                :contentObjects="impactsOfDisabilities"
                contentTypeTitle="Impact of Disability"
                :numberOfItems="2"
                v-if="iodVisibility"
              >
                <template #noContentMessage>
                  <a :href="toolkitUrls.impactOfDisability(aiFeatureVisibility, betaVisibility)" class="text-link">
                    Create an Impact of Disability statement
                  </a>
                </template>
              </LibraryFolderContentSection>
              <LibraryFolderContentSection
                :contentObjects="sortedPresentLevels"
                contentTypeTitle="Present Levels"
                :numberOfItems="2"
              >
                <template #noContentMessage>
                  <a :href="iodVisibility ? `${goalbookUrls.toolkit}/v/presentlevels/v2/session?ref=navbar` : `${toolkitUrls.presentlevelsWizard}`" class="text-link">
                    Create a present levels statement
                  </a>
                </template>
              </LibraryFolderContentSection>
              <LibraryFolderContentSection
                :contentObjects="goals"
                contentTypeTitle="Goals"
                :saved="true"
                :numberOfItems="2"
              >
                <template #noContentMessage>
                  <a :href="`${toolkitUrls.goalWizard}`" class="text-link"> Browse learning goals </a>
                </template>
              </LibraryFolderContentSection>
              <LibraryFolderContentSection
                :contentObjects="strategies"
                contentTypeTitle="Strategies"
                :numberOfItems="1"
                :saved="true"
                :gridView="false"
              >
                <template #noContentMessage>
                  <a :href="`${toolkitUrls.strategiesWizard}`" class="text-link"> Browse strategies </a>
                </template>
              </LibraryFolderContentSection>
              <LibraryFolderContentSection
                contentTypeTitle="Resources"
                :contentObjects="resources"
                :gridView="false"
              >
                <template #noContentMessage>
                  <a :href="`${toolkitUrls.browseAssessments}`" class="text-link"> Browse assessments </a>
                </template>
              </LibraryFolderContentSection>
            </div>

            <div class="col-span-12 lg:col-span-4">
              <div class="flex flex-col">
                <div class="flex flex-col gap-3">
                  <div class="flex items-center gap-2 pt-6 font-normal leading-[32px] text-attachment-title">
                    <h2 class="m-0 text-gray-900">Attachments</h2>
                    <span class="text-gray-700">{{ currentFolderAttachmentCount }}</span>
                  </div>

                  <GbButton
                    class="w-attachment-add-button"
                    gbTheme="blueOutlineSecondary"
                    size="small"
                    aria-label="Add attachment"
                    :disabled="attachmentLimitReached"
                    :tooltipText="attachmentLimitReached ? ATTACHMENT_LIMIT_TOOLTIP_TEXT : ''"
                    :ariaDescription="attachmentLimitReached ? ATTACHMENT_LIMIT_ARIA_DESCRIPTION : ''"
                    @click="handleAddAttachment"
                  >
                    <span class="flex items-center gap-0.5">
                      <span class="material-icons-round leading-none text-md text-blue-400">link</span>
                      <span>Add</span>
                    </span>
                  </GbButton>
                </div>

                <div class="mt-4">
                  <div v-if="currentFolderAttachmentCount === 0" class="text-md text-gray-600">
                    No attachments yet.
                  </div>

                  <div v-else class="flex flex-col gap-3">
                    <LinkCard
                      v-for="attachment in visibleAttachments"
                      :key="attachment.uuid"
                      :attachment="attachment"
                      @edit="handleAttachmentEdit"
                      @deleted="handleAttachmentDeleted"
                      @error="handleAttachmentError"
                    />

                    <button
                      v-if="hasMoreAttachments"
                      type="button"
                      class="text-link relative m-auto block cursor-pointer text-center pb-10"
                      @click="toggleAttachmentsView"
                    >
                      View {{ showAllAttachments ? 'less' : 'more' }}
                      <span class="material-icons-outlined absolute text-display-sm">{{
                        showAllAttachments ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                      }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ShareFolderModal v-model:externalOpen="openShare" v-if="canOpenShareModal && openShare" />
    <RenameFolderModal v-model:externalOpen="openRename" :reloadFolders="true" @folderEdited="loadFolder()" />
    <RolesAndPermissionsModal v-model:externalOpen="openContributors" @open-share="() => { if (canOpenShareModal) openShare = true }" />
    <ArchiveFolderModal
      v-model:externalOpen="openArchive"
      @folderArchived="handleFolderArchived"
    />
    <DeleteFolderModal v-model:externalOpen="openDelete" />
    <UnarchiveFirstModal
      v-if="isCurrentFolderArchived"
      v-model:externalOpen="openUnarchiveFirst"
    />
    <PremiumActionModal
        v-model:external-open="openPremiumAction"
      />
    <CopyFolderModal
      v-model="openCopy"
    />
    <RemoveContributorModal
      v-if="!hasAddRemoveContributorPerms"
      v-model:externalOpen="openRemoveContributor"
      :userUuid="userId"
    />
    <AttachmentModal
      v-model:externalOpen="openAttachmentModal"
      :folderUuid="props.uuid"
      :folderName="currentFolder?.name || ''"
      :contentUuid="attachmentToEdit?.content_uuid"
      :initialUrl="(attachmentToEdit?.content_body as any)?.url"
      :initialTitle="attachmentToEdit?.content_title"
      @saved="handleAttachmentSaved"
    />
    <ToastProvider>
      <ToastRoot
        :open="showAttachmentToast"
        @update:open="showAttachmentToast = $event"
        class="rounded-md shadow-sm py-2 px-4 items-center data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=end]:animate-swipeOut data-[swipe=cancel]:transition-[transform_200ms_ease-out]"
        :class="attachmentToastError ? 'bg-red-100 text-default border-red-300 border-1' : 'bg-black text-white'"
      >
        <div class="flex items-center">
          <span class="material-icons-outlined text-display-sm" v-if="!attachmentToastError">link</span>
          <span class="material-icons-round text-display-sm text-red-300" v-else>warning_amber</span>
          <span class="ml-2">{{ attachmentToastMessage }}</span>
        </div>
      </ToastRoot>
      <ToastViewport
        v-if="showAttachmentToast"
        :label="`Notification - attachments`"
        class="fixed top-0 left-0 right-0 flex justify-center pt-[70px] px-[25px] gap-[10px] w-full max-w-[100vw] m-0 list-none z-[999999] outline-none"
      />
    </ToastProvider>
  </div>
</template>